/**
 * **Où une fenêtre a le droit d'être.**
 *
 * Tout ce fichier est pur : aucune lecture du DOM, aucune horloge, aucun `window.innerWidth`. La
 * taille de l'écran et la place naturelle de la fenêtre lui sont DONNÉES. C'est ce qui permet à
 * `tests/window-placement.test.ts` de rejouer, sans navigateur, les situations qui ont fait sortir
 * une fenêtre de l'écran — et c'était impossible tant que ces calculs vivaient à l'intérieur d'un
 * hook React collé à `useLayoutEffect`.
 *
 * **L'invariant, en une phrase :** la fenêtre entière reste dans l'écran, sous la barre de menu et
 * au-dessus du dock. Pas son coin, pas sa barre de titre : sa boîte entière. Un enseignant qui
 * projette devant sa classe n'a pas de seconde chance pour rattraper une fenêtre à la souris.
 *
 * **Ce que ce fichier a fermé** (7 septembre 2026, mesuré à 1366 × 768). Une fenêtre réduite —
 * `display: none`, donc une boîte de mesure entièrement nulle — était bornée comme si elle mesurait
 * 0 × 0 pixel : tout l'écran lui était alors permis, et chaque événement `resize` la poussait de
 * 8px vers la droite et 38px vers le bas. Après une seule poignée redimensionnée à la souris (164
 * événements), la fenêtre rappelée depuis le dock revenait à **left = 1957, top = 8618** dans un
 * écran de 1366 × 768 : hors de portée, sans aucun moyen de la ramener. La correction est en deux
 * temps, et les deux comptent :
 *
 * 1. `clampOffset` ne borne plus **rien** contre une boîte sans surface (`hasBox`) : une fenêtre
 *    qu'on ne peut pas mesurer n'est pas une fenêtre qu'on peut déplacer.
 * 2. La place naturelle est lue sur la boîte de **mise en page** (`offsetLeft`/`offsetTop`, voir
 *    `useWindowDrag.ts`), que le `transform` n'affecte pas — au lieu d'être reconstituée en
 *    soustrayant le décalage d'un rectangle écran. Cette soustraction supposait que le décalage
 *    était appliqué ; en plein écran il ne l'est pas, et la borne dérivait à chaque mesure.
 *
 * D'où la propriété que le test vérifie et qui résume les deux : **borner est idempotent**.
 * `clamp(clamp(o)) === clamp(o)`, toujours. Une borne qui déplace ce qu'elle a déjà borné est une
 * borne qui dérive.
 */

/** Hauteur de la barre de menu (`desk.css`, § « Barre de menu ») : la fenêtre ne doit jamais
 * passer derrière, sans quoi sa barre de titre y deviendrait inatteignable. */
export const MENUBAR_HEIGHT = 30;

/** Marge minimale laissée visible sur chaque bord. */
export const EDGE_MARGIN = 8;

/**
 * La bande réservée au dock en bas de l'écran. C'est le `padding-bottom` de `.desk`
 * (docs/design/desktop-reference.md : « laisse la place à la barre de menu et au dock ») : le dock
 * mesure 64px de haut et flotte à 16px du bord, la fenêtre s'arrête donc bien au-dessus de lui.
 */
export const DOCK_RESERVE = 108;

/** Le pas de la cascade. Beaucoup plus large en horizontal qu'en vertical : la fenêtre fait déjà
 * presque toute la hauteur disponible (720px sur 736 à 900px de haut), la place à gagner est
 * latérale. Sur un grand écran, la borne laisse la composante verticale s'exprimer aussi. */
export const CASCADE_STEP_X = 34;
export const CASCADE_STEP_Y = 26;

/** Au-delà, la cascade recommence : cinq fenêtres est déjà plus que ce qu'un bureau porte
 * (deux outils, la consigne, « Mon compte », l'espace enseignant). */
export const CASCADE_SLOTS = 5;

export interface WindowOffset {
  readonly x: number;
  readonly y: number;
}

/** La place de la fenêtre **sans** décalage : sa boîte de mise en page, centrée par la grille du
 * bureau. Jamais un rectangle écran dont on aurait retiré le décalage — voir le commentaire de
 * tête. */
export interface NaturalRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** L'écran utile, en pixels CSS (`window.innerWidth` / `innerHeight` chez l'appelant). */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

/** Aucun décalage : la fenêtre à sa place naturelle. */
export const NO_OFFSET: WindowOffset = { x: 0, y: 0 };

/**
 * Une boîte mesurable. Une fenêtre réduite (`display: none`) rend 0 × 0 : on ne sait alors **rien**
 * d'elle, et prétendre la borner revient à lui autoriser tout l'écran. On ne la borne pas ; on
 * attend qu'elle revienne — `useWindowDrag` rejoue la borne au rendu qui la réaffiche.
 */
export function hasBox(natural: NaturalRect): boolean {
  return natural.width > 0 && natural.height > 0;
}

/** Le rectangle qu'occupera la fenêtre à l'écran une fois le décalage appliqué. */
export function placedRect(
  natural: NaturalRect,
  offset: WindowOffset,
): { left: number; top: number; right: number; bottom: number } {
  const left = natural.left + offset.x;
  const top = natural.top + offset.y;
  return { left, top, right: left + natural.width, bottom: top + natural.height };
}

/**
 * Ramène un décalage souhaité dans les bornes de l'écran : la fenêtre **entière** reste visible,
 * sous la barre de menu et au-dessus du dock.
 *
 * `Math.max(min…, …)` sur les bornes hautes : sur un écran plus petit que la fenêtre, l'intervalle
 * serait vide — on retombe alors sur la borne basse (coin haut-gauche accessible) plutôt que de
 * rendre un intervalle impossible.
 *
 * Le décalage rendu est **absolu**, jamais cumulatif : il ne dépend que de `natural`, `desired` et
 * `viewport`. C'est ce qui rend l'opération idempotente, donc rejouable à chaque rendu et à chaque
 * `resize` sans dériver d'un pixel.
 */
export function clampOffset(
  natural: NaturalRect,
  desired: WindowOffset,
  viewport: Viewport,
): WindowOffset {
  // Une fenêtre qu'on ne peut pas mesurer n'est pas bornée : on rend le décalage inchangé.
  if (!hasBox(natural)) return desired;

  const minLeft = EDGE_MARGIN;
  const minTop = MENUBAR_HEIGHT + EDGE_MARGIN;
  const maxLeft = Math.max(minLeft, viewport.width - natural.width - EDGE_MARGIN);
  const maxTop = Math.max(minTop, viewport.height - natural.height - DOCK_RESERVE);
  const left = Math.min(Math.max(natural.left + desired.x, minLeft), maxLeft);
  const top = Math.min(Math.max(natural.top + desired.y, minTop), maxTop);
  return { x: left - natural.left, y: top - natural.top };
}

/** Le décalage brut d'une place de cascade, avant bornage. La place 0 est centrée — c'est la
 * fenêtre unique de la référence verrouillée, elle ne bouge pas. */
export function cascadeOffset(slot: number): WindowOffset {
  const place = ((slot % CASCADE_SLOTS) + CASCADE_SLOTS) % CASCADE_SLOTS;
  return { x: place * CASCADE_STEP_X, y: place * CASCADE_STEP_Y };
}

/** Deux décalages identiques. Sert à ne PAS reposer un état inchangé : un nouvel objet rendu pour
 * rien relancerait un rendu, donc l'effet, donc le rendu suivant. */
export function sameOffset(a: WindowOffset, b: WindowOffset): boolean {
  return a.x === b.x && a.y === b.y;
}

/**
 * Le même écran, aux mêmes dimensions.
 *
 * C'est ce qui **déclenche** une reprise de bornage, et c'est délibérément la seule chose qui la
 * déclenche : une fenêtre ne devient hors de portée que si l'écran a rétréci. Tant qu'il n'a pas
 * changé de taille, il n'y a rien à reprendre — et surtout rien à mesurer.
 *
 * Ce qu'on évite avec ça (mesuré le 7 septembre 2026) : le bureau anime son `padding` sur 340 ms en
 * entrant et en sortant du plein écran (`desk.css`, `.desk`). Reborner pendant cette animation
 * revient à mesurer une géométrie qui n'existe qu'un instant — la fenêtre du fond y remontait de
 * 20 pixels et celle de devant se déplaçait de 26, à chaque projection. Or `desk.css` promet
 * exactement le contraire : « elles reviennent exactement où elles étaient, déplacement compris ».
 * Un aller-retour en plein écran ne change pas la taille de l'écran ; il ne déclenche donc rien.
 */
export function sameViewport(a: Viewport, b: Viewport): boolean {
  return a.width === b.width && a.height === b.height;
}
